import { useEffect, useState } from "react";
import { PackageX, X } from "lucide-react";
import { toast } from "sonner";
import { field, label } from "./IngredientEditor";
import { fmtQty, invApi } from "./api";

const REASONS = ["Spoiled", "Expired", "Dropped", "Burnt / overcooked", "Wrong order", "Other"];

/** "Toss it" — log spoiled/dropped stock as waste. Pass `ingredient` to skip the picker. */
export function WasteModal({ token, ingredient, onDone, onClose }) {
  const [list, setList] = useState([]);
  const [pick, setPick] = useState(ingredient?.id || "");
  const [qty, setQty] = useState("");
  const [reason, setReason] = useState("Spoiled");
  const [other, setOther] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (ingredient) return;
    invApi(token).get("/inventory/ingredients").then((r) => setList(r.data)).catch(() => {});
  }, [token, ingredient]);

  const ing = ingredient || list.find((i) => i.id === pick);

  const submit = async (e) => {
    e.preventDefault();
    const q = parseFloat(qty);
    if (!ing) return toast.error("Pick an ingredient");
    if (!(q > 0)) return toast.error("Enter how much was tossed");
    setBusy(true);
    try {
      const { data } = await invApi(token).post("/inventory/waste", { ingredient_id: ing.id, quantity: q, reason: reason === "Other" ? other.trim() || "Other" : reason });
      toast.success(`Tossed ${fmtQty(q)} ${data.unit} ${data.name}${data.waste_cost ? ` · $${data.waste_cost.toFixed(2)} lost` : ""}`);
      onDone?.(data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't log waste"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-3" onClick={onClose} data-testid="waste-modal">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl w-full max-w-sm p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div className="font-black text-stone-800 text-sm uppercase tracking-widest flex items-center gap-2"><PackageX className="w-4 h-4 text-brand-red" /> Toss it</div>
          <button type="button" onClick={onClose} aria-label="Close" className="text-stone-400" data-testid="waste-close"><X className="w-5 h-5" /></button>
        </div>
        {!ingredient && (
          <select value={pick} onChange={(e) => setPick(e.target.value)} className={field} data-testid="waste-pick-select" autoFocus>
            <option value="">What got tossed?</option>
            {list.map((i) => <option key={i.id} value={i.id}>{i.name} ({fmtQty(i.current_stock)} {i.unit})</option>)}
          </select>
        )}
        {ing && <div className="text-xs text-stone-500">{ing.name} · on hand <b>{fmtQty(ing.current_stock)} {ing.unit}</b>{ing.cost_per_unit ? ` · $${ing.cost_per_unit}/${ing.unit}` : ""}</div>}
        <label className="block"><span className={label}>Quantity tossed{ing ? ` (${ing.unit})` : ""}</span>
          <input value={qty} onChange={(e) => setQty(e.target.value)} type="number" step="any" min="0" inputMode="decimal" autoFocus={!!ingredient} data-testid="waste-qty-input"
            className="w-full px-4 py-3 rounded-2xl border-2 border-stone-200 text-2xl font-black text-center tabular-nums outline-none focus:border-brand-red" /></label>
        <div className="flex flex-wrap gap-1.5" data-testid="waste-reasons">
          {REASONS.map((r) => (
            <button key={r} type="button" onClick={() => setReason(r)} data-testid={`waste-reason-${r.split(" ")[0].toLowerCase()}`}
              className={`px-3 py-1.5 rounded-full text-[10px] uppercase tracking-widest font-black border ${reason === r ? "bg-brand-red text-white border-brand-red" : "border-stone-200 text-stone-600"}`}>{r}</button>
          ))}
        </div>
        {reason === "Other" && <input value={other} onChange={(e) => setOther(e.target.value)} placeholder="What happened?" className={field} data-testid="waste-other-input" />}
        <button type="submit" disabled={busy} data-testid="waste-submit" className="w-full py-3 rounded-full bg-brand-red text-white text-xs uppercase tracking-widest font-black disabled:opacity-50">{busy ? "Logging…" : "Log as waste"}</button>
      </form>
    </div>
  );
}
